import { isPlatformBrowser } from '@angular/common'
import { inject, Injectable, PLATFORM_ID } from '@angular/core'

import { AppConfigService } from 'app/app-config.service'

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

interface AnalyticsProduct {
  id?: number | string
  name?: string
  final_price?: number
}

interface AnalyticsCartItem {
  id?: number | string
  name?: string
  category?: string
  color?: string
  price?: number
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private platformId = inject(PLATFORM_ID)
  private browser = isPlatformBrowser(this.platformId)
  private appConfigService = inject(AppConfigService)
  private previousPageLocation: string | undefined

  /** Send any GA4 event */
  event(name: string, params: Record<string, unknown> = {}) {
    const storefrontParams = this.storefrontParams()
    if (storefrontParams && window.gtag) {
      window.gtag('event', name, { ...params, ...storefrontParams })
    }
  }

  /** SPA navigation to virtual page_view */
  trackPageViews(pagePath: string) {
    const storefrontParams = this.storefrontParams()
    if (storefrontParams && window.gtag) {
      const pageLocation = this.urlWithoutQuery(window.location.href)

      window.gtag('event', 'page_view', {
        ...storefrontParams,
        page_location: pageLocation,
        page_path: this.pathWithoutQuery(pagePath),
        page_referrer: this.previousPageLocation ?? this.urlWithoutQuery(document.referrer),
        page_title: document.title
      })

      this.previousPageLocation = pageLocation
    }
  }

  /** Track when a user views a product */
  trackViewItem(product: AnalyticsProduct, quantity = 1) {
    this.event('view_item', {
      currency: 'USD',
      value: (product.final_price ?? 0) / 100,
      items: [
        {
          item_id: product.id?.toString(),
          item_name: product.name,
          price: (product.final_price ?? 0) / 100,
          quantity
        }
      ]
    })
  }

  /** Track when a user adds a product to cart */
  trackAddToCart(item: AnalyticsCartItem, quantity: number) {
    const price = item.price ?? 0
    this.event('add_to_cart', {
      currency: 'USD',
      value: (price / 100) * quantity,
      items: [
        {
          item_id: item.id?.toString(),
          item_name: item.name,
          item_category: item.category || 'Product',
          item_variant: item.color,
          price: price / 100,
          quantity
        }
      ]
    })
  }

  private storefrontParams(): Record<string, string> | undefined {
    if (!this.browser) {
      return undefined
    }

    const storefront = this.appConfigService.getAppConfig
    if (storefront?.id == null) {
      return undefined
    }

    return {
      storefront_id: String(storefront.id),
      storefront_name: String(storefront.name ?? '')
    }
  }

  private urlWithoutQuery(value: string): string {
    if (!value) {
      return ''
    }

    const url = new URL(value)
    url.search = ''
    url.hash = ''
    return url.href
  }

  private pathWithoutQuery(pagePath: string): string {
    return pagePath.split(/[?#]/, 1)[0]
  }
}
